import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ChangePasswordRequest, CheckForgotPasswordRequest, ForgotPasswordRequest, SetPasswordRequest } from '../models/forgot-password.model';
import { LoginRequest, LoginResponse, PegasusLoginResponse } from '../models/login.model';
import { RegisterRequest, RegisterResponse } from '../models/register.model';
import { User } from '../models/user.model';
// eslint-disable-next-line max-len
import { ResendVerificationCodeRequest, ResendVerificationCodeResponse, VerifyUserRequest, VerifyUserResponse } from '../models/verification.model';

const endpoints = {
  login: (type: string) => '/auth/v2/login?type=' + type,
  register: '/auth/register',
  verifyUser: '/auth/verify-user',
  resendVerificationCode: '/auth/resend-verification-code',
  sendForgotPasswordCode: '/auth/send-forgot-password-code',
  checkForgotPasswordCode: '/auth/check-forgot-password-code',
  changePassword: '/auth/change-password',
  setPassword: '/auth/set-password',
  pegasusLogin: (role: string, redirectUrl?: string) => {
    const baseUrl = '/pegasus-auth/login-url?client=web&role=' + role;
    return redirectUrl ? baseUrl + '&redirectUrl=' + encodeURIComponent(redirectUrl) : baseUrl;
  },
  checkCode: (code: string, state: string) => '/pegasus-auth/check-code/' + code + '?state=' + state
};

@Injectable()
export class AuthService {
  public currentUser: Observable<User>;
  public currentUserSubject: BehaviorSubject<User>;

  constructor(
    private http: HttpClient
  ) {
    this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('currentAuth')));
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  login(payload: LoginRequest, type = 'user'): Observable<LoginResponse> {
    const url = environment.apiBaseUrl + endpoints.login(type);
    return this.http.post(url, payload).pipe(
      map((res: LoginResponse) => {
        this.saveAuthToken(res);
        return res;
      })
    );
  }

  async saveAuthToken(res: LoginResponse | VerifyUserResponse, fromVerify = false): Promise<void> {
    if (res && res.success) {
      const user = res.user;
      user.token = res.token;
      localStorage.setItem('currentAuth', JSON.stringify(user));
      this.currentUserSubject.next(user);
    } else {
      localStorage.removeItem('currentAuth');
      this.currentUserSubject.next(null);
    }
  }

  register(payload: RegisterRequest): Observable<RegisterResponse> {
    const url = environment.apiBaseUrl + endpoints.register;
    return this.http.post<RegisterResponse>(url, payload);
  }

  checkCode(code: string, state: string): Observable<LoginResponse> {
    const url = environment.apiBaseUrl + endpoints.checkCode(code, state);
    return this.http.get<LoginResponse>(url);
  }

  verifyUser(payload: VerifyUserRequest): Observable<VerifyUserResponse> {
    const url = environment.apiBaseUrl + endpoints.verifyUser;
    return this.http.post<VerifyUserResponse>(url, payload).pipe(
      map((res: VerifyUserResponse) => {
        this.saveAuthToken(res, true);
        return res;
      })
    );
  }

  resendVerificationCode(payload: ResendVerificationCodeRequest): Observable<ResendVerificationCodeResponse> {
    const url = environment.apiBaseUrl + endpoints.resendVerificationCode;
    return this.http.post<ResendVerificationCodeResponse>(url, payload);
  }

  logout(): void {
    localStorage.removeItem('currentAuth');
    this.currentUserSubject.next(null);
  }

  sendForgotPasswordCode(payload: ForgotPasswordRequest): Observable<any> {
    const url = environment.apiBaseUrl + endpoints.sendForgotPasswordCode;
    return this.http.post<any>(url, payload);
  }

  checkForgotPasswordCode(payload: CheckForgotPasswordRequest): Observable<any> {
    const url = environment.apiBaseUrl + endpoints.checkForgotPasswordCode;
    return this.http.post<any>(url, payload);
  }

  changePassword(payload: ChangePasswordRequest): Observable<any> {
    const url = environment.apiBaseUrl + endpoints.changePassword;
    return this.http.post<any>(url, payload);
  }

  setPassword(payload: SetPasswordRequest): Observable<any> {
    const url = environment.apiBaseUrl + endpoints.setPassword;
    return this.http.post<any>(url, payload);
  }

  pegasusLogin(role: string): Observable<PegasusLoginResponse> {
    // Get current window location for localhost development
    const currentOrigin = window.location.origin;
    const redirectUrl = currentOrigin + '/auth/check-token';

    const url = environment.apiBaseUrl + endpoints.pegasusLogin(role, redirectUrl);
    console.log('Pegasus login URL with redirect:', url);
    return this.http.get<PegasusLoginResponse>(url);
  }

}
